'use client';

import * as ScrollArea from '@radix-ui/react-scroll-area';
import {
	IconCategory,
	IconChevronRight,
	IconHash,
	IconSearch,
	IconX,
} from '@tabler/icons-react';
import { Link } from 'next-view-transitions';
import { cn } from '@/lib/utils';
import { Article, CategoryWithCount } from '@/types/types';
import { BlogSidebarSection } from './blog-sidebar-section';

interface MobileCategoryDrawerProps {
	categories: CategoryWithCount[];
	article?: Article;
	searchQuery: string;
	setSearchQuery: (query: string) => void;
	isMobileDrawerOpen: boolean;
	setIsMobileDrawerOpen: (isOpen: boolean) => void;
}

export function MobileCategoryDrawer({
	categories,
	article,
	searchQuery,
	setSearchQuery,
	isMobileDrawerOpen,
	setIsMobileDrawerOpen,
}: MobileCategoryDrawerProps) {
	// Filter categories based on search query
	const filteredCategories = categories.filter(
		category =>
			category.name?.toLowerCase().includes(searchQuery.toLowerCase()) ??
			false,
	);

	return (
		<>
			{/* Mobile Floating Action Button */}
			<button
				id="mobile-category-button"
				onClick={() => setIsMobileDrawerOpen(!isMobileDrawerOpen)}
				className="fixed bottom-6 left-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-primary shadow-lg transition-transform hover:scale-105 lg:hidden"
				aria-label="Open categories">
				<IconCategory size={20} className="text-white" />
			</button>

			{/* Mobile Categories Drawer */}
			{/* z-[3000000000] is a hack to ensure the drawer is above the other third party widget */}
			<div
				id="mobile-category-drawer"
				className={cn(
					'fixed inset-0 z-[3000000000] transform bg-black/50 backdrop-blur-sm transition-opacity duration-300 lg:hidden',
					isMobileDrawerOpen
						? 'opacity-100'
						: 'pointer-events-none opacity-0',
				)}>
				<div
					className={cn(
						'absolute bottom-0 left-0 right-0 transform rounded-t-2xl bg-white transition-transform duration-300',
						isMobileDrawerOpen
							? 'translate-y-0'
							: 'translate-y-full',
					)}>
					<div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
						<h3 className="flex items-center gap-2 font-semibold text-neutral-900">
							<IconHash size={18} className="text-primary" />
							Categories
						</h3>
						<button
							onClick={() => setIsMobileDrawerOpen(false)}
							className="rounded-full p-1 hover:bg-neutral-100"
							aria-label="Close drawer">
							<IconX size={20} className="text-neutral-500" />
						</button>
					</div>
					<ScrollArea.Root className="w-full" type="auto">
						<ScrollArea.Viewport className="max-h-[70vh] w-full">
							<BlogSidebarSection className="p-4">
								<div className="mb-4">
									<div className="relative">
										<input
											type="text"
											placeholder="Search categories..."
											value={searchQuery}
											onChange={e =>
												setSearchQuery(e.target.value)
											}
											className="w-full rounded-md border border-neutral-200 bg-neutral-50 px-4 py-2 text-sm placeholder-neutral-400 focus:border-primary/50 focus:outline-none focus:ring-1 focus:ring-primary/50"
										/>
										<IconSearch
											size={16}
											className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400"
										/>
									</div>
								</div>
								<div className="divide-y divide-neutral-100">
									{filteredCategories.length > 0 ? (
										filteredCategories.map(category => (
											<Link
												key={category.id}
												href={`/blog?category=${category.slug}`}
												onClick={() =>
													setIsMobileDrawerOpen(false)
												}
												className={cn(
													'group flex items-center justify-between py-3.5 transition-all duration-200',
													article?.categories?.some(
														cat =>
															cat.id ===
															category.id,
													)
														? 'text-primary'
														: 'text-neutral-700',
												)}>
												<span
													className={cn(
														'text-sm transition-colors',
														article?.categories?.some(
															cat =>
																cat.id ===
																category.id,
														)
															? 'font-medium'
															: 'group-hover:text-neutral-900',
													)}>
													{category.name || 'Unnamed'}
												</span>
												<div className="flex items-center gap-3">
													<span
														className={cn(
															'rounded-full px-2 py-0.5 text-xs font-medium tabular-nums',
															article?.categories?.some(
																cat =>
																	cat.id ===
																	category.id,
															)
																? 'bg-primary/10 text-primary'
																: 'bg-neutral-100 text-neutral-600 group-hover:bg-neutral-200',
														)}>
														{category.articles_count ||
															0}
													</span>
													<IconChevronRight
														size={16}
														className={cn(
															'transform transition-transform group-hover:translate-x-0.5',
															article?.categories?.some(
																cat =>
																	cat.id ===
																	category.id,
															)
																? 'text-primary'
																: 'text-neutral-400',
														)}
													/>
												</div>
											</Link>
										))
									) : (
										<div className="flex flex-col items-center justify-center py-8 text-center">
											<IconSearch
												size={24}
												className="mb-2 text-neutral-300"
											/>
											<p className="text-sm text-neutral-500">
												No categories found
											</p>
											<button
												onClick={() =>
													setSearchQuery('')
												}
												className="mt-2 text-xs font-medium text-primary hover:text-primary/80">
												Clear search
											</button>
										</div>
									)}
								</div>
							</BlogSidebarSection>
						</ScrollArea.Viewport>
						<ScrollArea.Scrollbar
							className="flex touch-none select-none p-0.5 transition-colors duration-150 ease-out hover:bg-neutral-100 data-[orientation=horizontal]:h-2.5 data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col"
							orientation="vertical">
							<ScrollArea.Thumb className="relative flex-1 rounded-[10px] bg-neutral-300 before:absolute before:left-1/2 before:top-1/2 before:h-full before:min-h-[44px] before:w-full before:min-w-[44px] before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']" />
						</ScrollArea.Scrollbar>
						<ScrollArea.Corner className="bg-neutral-200" />
					</ScrollArea.Root>
				</div>
			</div>
		</>
	);
}
